import { Alert, Typography, styled } from "@mui/material";

import { StarburstIcon } from "@citrine/atoms/icons";
import { Column, Row } from "@citrine/atoms/layout";

interface MessageBubbleProps {
  isUser?: boolean;
}

// The user's turn is a compact grey bubble (right). The assistant's turn has no
// bubble — it's full-width content on the panel, so rich markdown/tables/alerts
// have room and the prose carries it (ChatGPT/Claude style).
export const MessageBubble = styled("div", {
  shouldForwardProp: (prop) => prop !== "isUser",
})<MessageBubbleProps>(({ isUser, theme }) =>
  isUser
    ? {
        alignSelf: "flex-end",
        backgroundColor: theme.palette.grey[200],
        borderRadius: theme.shape.borderRadiusLg,
        color: theme.palette.text.primary,
        maxWidth: "85%",
        padding: theme.spacing(1, 1.5),
        whiteSpace: "pre-wrap",
      }
    : {
        alignSelf: "stretch",
        color: theme.palette.text.primary,
        maxWidth: "100%",
      },
);

export const MessageList = styled(Column)(({ theme }) => ({
  background: theme.palette.common.white,
  // Fill the panel between header and input and scroll internally, so the
  // scrollbar sits flush at the panel's right edge (minHeight: 0 lets a flex
  // child shrink below its content height so it actually scrolls).
  flex: 1,
  gap: theme.spacing(1.5),
  minHeight: 0,
  overflowY: "auto",
  padding: theme.spacing(2),
  scrollBehavior: "smooth",
}));

// Errors are system conditions, not the agent's content, so they render as
// standalone (standard) Alerts outside the message bubble — spanning the full
// panel width to read as a system-level notice rather than a chat turn.
export const ErrorAlert = styled(Alert)({
  alignSelf: "stretch",
});

// A runtime event (tool call, retrieval, step progress) is process, not
// content — a quiet status line that recedes beneath the prose rather than a
// boxed chip. The agent glyph ties it to the Omni/agent vocabulary.
export const EventLine = styled(Row)(({ theme }) => ({
  gap: theme.spacing(0.75),
  margin: theme.spacing(1, 0, 0.25),
}));

export const EventGlyph = styled(StarburstIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  flexShrink: 0,
  fontSize: theme.spacing(1.75),
}));

// While streaming, the agent glyph pulses — scaling out from its center while
// fading — to signal "working", in place of a blinking caret. Extends EventGlyph
// so the icon/color stay identical to the event line (one vocabulary).
export const StreamingIndicator = styled(EventGlyph)(({ theme }) => ({
  animation: "pulse 1.5s ease-in-out infinite",
  // A touch larger than the event-line glyph so it reads as the active cursor.
  fontSize: theme.spacing(3),
  // Separate it from the content above.
  marginTop: theme.spacing(1),
  transformOrigin: "center",
  verticalAlign: "text-bottom",

  "@keyframes pulse": {
    "0%, 100%": { opacity: 0.4, transform: "scale(0.6)" },
    "50%": { opacity: 1, transform: "scale(1)" },
  },

  // Hold the glyph steady for users who opt out of motion.
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
}));

export const Text = styled(Typography)({ wordBreak: "break-word" });

export const WelcomeText = styled(Typography)(({ theme }) => ({
  alignSelf: "center",
  color: theme.palette.text.secondary,
  padding: theme.spacing(4, 2),
}));
